import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../../services/api';
import {
  Plus, Trash2, Save, Edit3, X, Tag, Calendar, Clock, ToggleLeft, ToggleRight, FileText, Zap
} from 'lucide-react';
import ScopeSelector from '../components/ScopeSelector';
import { PageTooltip } from '../components/TooltipSystem';

export default function AdminPromozioni() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', promo_type: 'custom', valid_from: '', valid_to: '', conditions: '', is_active: true });
  const [scope, setScope] = useState('brand');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await adminApi.get('/admin/promotions');
      setPromos(res.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm({ name: '', description: '', promo_type: 'custom', valid_from: '', valid_to: '', conditions: '', is_active: true });
    setEditId(null);
    setShowForm(false);
  };

  const openEdit = (p) => {
    setForm({ name: p.name, description: p.description, promo_type: p.promo_type || 'custom', valid_from: p.valid_from || '', valid_to: p.valid_to || '', conditions: p.conditions || '', is_active: p.is_active });
    setEditId(p.id);
    setScope(p.scope || 'brand');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Inserisci il nome'); return; }
    if (!form.description.trim()) { toast.error('Inserisci la descrizione'); return; }
    setSaving(true);
    try {
      const payload = { ...form, scope };
      if (editId) {
        await adminApi.put(`/admin/promotions/${editId}`, payload);
        toast.success('Promozione aggiornata');
      } else {
        await adminApi.post('/admin/promotions', payload);
        toast.success('Promozione creata');
      }
      resetForm();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa promozione?')) return;
    try {
      await adminApi.delete(`/admin/promotions/${id}`);
      toast.success('Eliminata');
      load();
    } catch { toast.error('Errore'); }
  };

  const handleToggle = async (p) => {
    try {
      await adminApi.put(`/admin/promotions/${p.id}`, { is_active: !p.is_active });
      load();
      toast.success(p.is_active ? 'Disattivata' : 'Attivata');
    } catch { toast.error('Errore'); }
  };

  const fmtDate = (d) => d ? new Date(d + 'T00:00').toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-3xl" data-testid="admin-promozioni-page">
      <PageTooltip page="promozioni" />
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl md:text-3xl tracking-tight font-bold text-[#1A1A1A]">Promozioni</h1>
          <p className="text-[#64748B] text-xs mt-0.5">Crea e gestisci le promozioni per il broadcast WhatsApp</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-promo-btn"
          className="flex items-center gap-1.5 px-3 py-2 bg-[#D44E20] text-white text-xs font-bold rounded-xl hover:bg-[#B83D15] transition-colors shadow-sm">
          <Plus size={14} /> Nuova promo
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm mb-5 space-y-4" data-testid="promo-form">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-[#1A1A1A] text-sm">{editId ? 'Modifica promozione' : 'Nuova promozione'}</h3>
            <button onClick={resetForm} className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-[#64748B] hover:bg-gray-200"><X size={14} /></button>
          </div>

          <div>
            <label className="text-[11px] text-[#64748B] font-medium block mb-1">Nome promozione <span className="text-[#D44E20]">*</span></label>
            <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Es. Happy Hour 2x1" maxLength={80} data-testid="promo-name-input"
              className="w-full h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] placeholder:text-[#ccc] focus:border-[#D44E20] focus:outline-none" />
          </div>

          <div>
            <label className="text-[11px] text-[#64748B] font-medium block mb-1">Descrizione (testo del messaggio WhatsApp) <span className="text-[#D44E20]">*</span></label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Es. Cocktail 2x1 ogni venerdì dalle 18 alle 20! Porta un amico e ricevi uno shot omaggio."
              rows={3} maxLength={300} data-testid="promo-desc-input"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm text-[#1A1A1A] placeholder:text-[#ccc] focus:border-[#D44E20] focus:outline-none resize-none" />
            <p className="text-[10px] text-[#999] mt-0.5">Questo testo viene usato come variabile nel messaggio WhatsApp. Max 300 caratteri. ({form.description.length}/300)</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-[#64748B] font-medium block mb-1">Valida dal</label>
              <input type="date" value={form.valid_from} onChange={e => setForm(f => ({ ...f, valid_from: e.target.value }))}
                data-testid="promo-from-input"
                className="w-full h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
            </div>
            <div>
              <label className="text-[11px] text-[#64748B] font-medium block mb-1">Valida fino al</label>
              <input type="date" value={form.valid_to} onChange={e => setForm(f => ({ ...f, valid_to: e.target.value }))}
                data-testid="promo-to-input"
                className="w-full h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
            </div>
          </div>

          <div>
            <label className="text-[11px] text-[#64748B] font-medium block mb-1">Condizioni (opzionale)</label>
            <input type="text" value={form.conditions} onChange={e => setForm(f => ({ ...f, conditions: e.target.value }))}
              placeholder="Es. Solo al bancone, Min. 20€ di spesa" maxLength={100} data-testid="promo-conditions-input"
              className="w-full h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] placeholder:text-[#ccc] focus:border-[#D44E20] focus:outline-none" />
          </div>

          {/* Preview */}
          <div className="bg-[#E7F8EC] border border-[#C8E6C9] rounded-xl p-3">
            <p className="text-[10px] text-[#4CAF50] font-medium mb-1.5">Anteprima messaggio WhatsApp</p>
            <p className="text-[12px] text-[#1B5E20] whitespace-pre-line font-mono leading-relaxed">
              {`Ciao Marco!\n\nBuone notizie da Il tuo locale: ${form.description || '...'}\n\nNon perdere questa occasione, ti aspettiamo presto!`}
            </p>
          </div>

          <ScopeSelector value={scope} onChange={setScope} />

          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving} data-testid="save-promo-btn"
              className="flex-1 h-10 bg-[#D44E20] text-white text-sm font-bold rounded-xl flex items-center justify-center gap-2 hover:bg-[#B83D15] disabled:opacity-50 transition-colors">
              <Save size={14} /> {saving ? 'Salvataggio...' : editId ? 'Aggiorna' : 'Crea promozione'}
            </button>
            <button onClick={resetForm} className="h-10 px-5 bg-slate-100 text-[#64748B] text-sm font-medium rounded-xl hover:bg-gray-200 transition-colors">Annulla</button>
          </div>
        </div>
      )}

      {/* Promo list */}
      {promos.length === 0 && !showForm ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-100 shadow-sm">
          <Tag size={32} className="mx-auto mb-3 text-[#ddd]" />
          <p className="text-[#64748B] text-sm font-medium">Nessuna promozione</p>
          <p className="text-[#aaa] text-xs mt-1">Crea la tua prima promozione per inviarla via broadcast ai clienti</p>
        </div>
      ) : (
        <div className="space-y-3">
          {promos.map(p => (
            <div key={p.id} className={`bg-white rounded-2xl border overflow-hidden shadow-sm transition-all ${p.is_active ? 'border-slate-100' : 'border-slate-100 opacity-60'}`} data-testid={`promo-card-${p.id}`}>
              <div className="p-4 flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${p.is_active ? 'bg-[#D44E20]/10' : 'bg-slate-100'}`}>
                  <Zap size={16} className={p.is_active ? 'text-[#D44E20]' : 'text-[#aaa]'} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <h3 className="font-semibold text-[#1A1A1A] text-sm">{p.name}</h3>
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-md ${p.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-[#999]'}`}>
                      {p.is_active ? 'ATTIVA' : 'INATTIVA'}
                    </span>
                  </div>
                  <p className="text-[#64748B] text-xs line-clamp-2">{p.description}</p>
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    {(p.valid_from || p.valid_to) && (
                      <span className="text-[10px] text-[#64748B] flex items-center gap-1">
                        <Calendar size={10} /> {fmtDate(p.valid_from)}{p.valid_from && p.valid_to ? ' - ' : ''}{fmtDate(p.valid_to)}
                      </span>
                    )}
                    {p.conditions && (
                      <span className="text-[10px] text-[#64748B] flex items-center gap-1">
                        <FileText size={10} /> {p.conditions}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => handleToggle(p)} data-testid={`toggle-promo-${p.id}`}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-[#aaa] hover:bg-slate-100 hover:text-[#64748B] transition-colors">
                    {p.is_active ? <ToggleRight size={16} className="text-[#3ECF8E]" /> : <ToggleLeft size={16} />}
                  </button>
                  <button onClick={() => openEdit(p)} data-testid={`edit-promo-${p.id}`}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-[#aaa] hover:bg-slate-100 hover:text-[#64748B] transition-colors">
                    <Edit3 size={13} />
                  </button>
                  <button onClick={() => handleDelete(p.id)} data-testid={`delete-promo-${p.id}`}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-[#aaa] hover:bg-red-50 hover:text-red-500 transition-colors">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info */}
      <div className="mt-5 px-4 py-3 bg-blue-50 border border-blue-100 rounded-xl">
        <p className="text-[11px] text-blue-700">
          Le promozioni attive saranno disponibili come opzione nel template broadcast "Promozione Speciale".
          La descrizione verrà usata come testo del messaggio WhatsApp.
        </p>
      </div>
    </div>
  );
}
